/**
 * BGV Status Widget
 * 
 * Shows real-time BGV integration status and social media metrics
 */

import React from 'react'
import {
  CheckCircle2,
  AlertCircle,
  XCircle,
  RefreshCw
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useConnectionHealth } from '@/features/social/hooks'

interface BGVStatusWidgetProps {
  compact?: boolean
}

export default function BGVStatusWidget({ compact = false }: BGVStatusWidgetProps) {
  const { data: connectionHealth, isLoading, refetch } = useConnectionHealth()

  const getStatusIcon = () => {
    if (isLoading) return <RefreshCw className="h-4 w-4 animate-spin" />
    if (!connectionHealth) return <XCircle className="h-4 w-4 text-destructive" />

    switch (connectionHealth.status) {
      case 'healthy':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />
      case 'degraded':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />
      case 'unhealthy':
        return <XCircle className="h-4 w-4 text-destructive" />
      default:
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />
    }
  }

  const getStatusColor = () => {
    if (!connectionHealth) return 'text-muted-foreground'

    switch (connectionHealth.status) {
      case 'healthy': return 'text-green-600'
      case 'degraded': return 'text-yellow-600'
      case 'unhealthy': return 'text-destructive'
      default: return 'text-muted-foreground'
    }
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-lg">
        {getStatusIcon()}
        <span className={`text-sm font-medium ${getStatusColor()}`}>
          BGV: {connectionHealth?.status || 'Unknown'}
        </span>
        <Button variant="ghost" size="sm" onClick={() => refetch()} className="h-6 w-6 p-0">
          <RefreshCw className="h-3 w-3" />
        </Button>
      </div>
    )
  }
  return null
}

